import React from 'react';
import { Sparkles, CheckCircle } from 'lucide-react';
import { uniqueFeatures } from '../data/mock';

const UniqueFeatures = () => {
  return (
    <section id="why-zosia" className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(251,146,60,0.1),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(245,158,11,0.1),transparent_50%)]"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-orange-500/20 to-amber-500/20 backdrop-blur-sm rounded-full border border-orange-500/30 mb-6">
            <Sparkles className="w-5 h-5 text-orange-400" />
            <span className="text-orange-400 font-semibold">Przełom na rynku</span>
          </div>

          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Czym Zosia mała nauczycielka wyróżnia się{' '}
            <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
              na tle konkurencji?
            </span>
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Inne roboty edukacyjne tylko migają i piszczą. Zosia mała nauczycielka? NAPRAWDĘ uczy i komunikuje się - bez ekranów i bez internetu!
          </p>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {uniqueFeatures.map((feature) => (
            <div
              key={feature.id}
              className={`relative group ${
                feature.highlight
                  ? 'md:col-span-2 lg:col-span-1 bg-gradient-to-br from-orange-500/20 to-amber-500/20 border-2 border-orange-500/50'
                  : 'bg-slate-800/50 border border-slate-700'
              } backdrop-blur-sm rounded-2xl p-8 hover:shadow-2xl hover:shadow-orange-500/10 transition-all duration-300`}
            >
              {/* Highlight badge */}
              {feature.highlight && (
                <div className="absolute -top-3 -right-3 px-4 py-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full text-xs font-bold shadow-lg animate-pulse">
                  UNIKALNE!
                </div>
              )}

              {/* Content */}
              <div className="flex items-start space-x-3">
                <CheckCircle className={`flex-shrink-0 w-6 h-6 mt-1 ${
                  feature.highlight ? 'text-orange-400' : 'text-emerald-400'
                }`} />
                <div>
                  <h3 className={`text-xl font-bold mb-3 ${
                    feature.highlight ? 'text-white' : 'text-slate-200'
                  }`}>
                    {feature.title}
                  </h3>
                  <p className="text-slate-300 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>

              {/* Hover effect */}
              <div className={`absolute bottom-0 right-0 w-24 h-24 ${
                feature.highlight ? 'bg-orange-500/10' : 'bg-slate-700/10'
              } rounded-tl-full transform scale-0 group-hover:scale-100 transition-transform duration-300`}></div>
            </div>
          ))}
        </div>

        {/* Comparison callout */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-3xl p-8 sm:p-12 shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4">
                  Inne roboty edukacyjne
                </h3>
                <ul className="space-y-3 text-orange-50">
                  <li className="flex items-center space-x-2">
                    <span className="text-2xl">❌</span>
                    <span>Tylko dźwięki i światełka</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-2xl">❌</span>
                    <span>Maksymalnie 2-3 języki</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-2xl">❌</span>
                    <span>Wymagają ekranów i aplikacji</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-2xl">❌</span>
                    <span>Ograniczona funkcjonalność</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border-2 border-white/30">
                <h3 className="text-3xl font-bold mb-4 flex items-center">
                  <Sparkles className="w-8 h-8 mr-3" />
                  Zosia mała nauczycielka
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-2 font-semibold">
                    <span className="text-2xl">✅</span>
                    <span>Mówi PO POLSKU pełnymi zdaniami!</span>
                  </li>
                  <li className="flex items-center space-x-2 font-semibold">
                    <span className="text-2xl">✅</span>
                    <span>9 języków (Polski + 8 obcych)</span>
                  </li>
                  <li className="flex items-center space-x-2 font-semibold">
                    <span className="text-2xl">✅</span>
                    <span>Bez ekranu - bezpieczna nauka</span>
                  </li>
                  <li className="flex items-center space-x-2 font-semibold">
                    <span className="text-2xl">✅</span>
                    <span>Nieograniczone możliwości</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-xl font-bold">
                🏆 Zosia mała nauczycielka to PIERWSZY i JEDYNY taki robot na polskim rynku!
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UniqueFeatures;
